import type { TImageElement } from "platejs";
import {
  type PlateElementProps,
  PlateElement,
  useEditorRef,
  usePath,
  useReadOnly,
  useSelected,
} from "platejs/react";
import type { PointerEvent } from "react";
import { memo, useCallback, useRef, useState } from "react";
import CircleSpinner from "../../circle-spinner.svg";
import { Flex } from "../../flex";
import { cn } from "../../tailwind-utils";
import { Text } from "../../text";

/** Sentinel URL prefix used by handleImageSelect while an upload is in flight. */
export const PENDING_IMAGE_URL_PREFIX = "data:pending/";

export const ImageElement = memo(function ImageElement(
  props: PlateElementProps<TImageElement>,
) {
  const { url, width } = props.element;
  const editor = useEditorRef();
  const path = usePath();
  const selected = useSelected();
  const readOnly = useReadOnly();

  // Non-null only during an active drag; falls back to the persisted element width
  const [resizingWidth, setResizingWidth] = useState<number | null>(null);

  const imgRef = useRef<HTMLImageElement>(null);

  const cssWidth =
    resizingWidth !== null
      ? resizingWidth
      : typeof width === "string" || typeof width === "number"
        ? width
        : undefined;

  const handleWidthChange = useCallback((w: number) => {
    setResizingWidth(w);
  }, []);

  const handleWidthCommit = useCallback(
    (finalWidth: number) => {
      // width stored as a number so the HTML email serializer emits a valid attribute
      editor.tf.setNodes({ width: finalWidth }, { at: path });
      setResizingWidth(null);
    },
    [editor, path],
  );

  const showHandles = selected && !readOnly;

  // Show a loading placeholder when the URL is our upload-in-progress sentinel.
  // Only match the `data:pending/` prefix set by handleImageSelect — not all
  // data: URLs, since existing content may contain legitimate base64 images.
  const isUploading =
    typeof url === "string" && url.startsWith(PENDING_IMAGE_URL_PREFIX);

  return (
    <PlateElement {...props} className="py-1">
      <div className="relative inline-block max-w-full">
        {isUploading ? (
          <Flex
            align="center"
            className={cn(
              "rounded-sm bg-muted text-muted-foreground",
              selected && "ring-2 ring-blue-500 ring-offset-0",
            )}
            gap="sm"
            px="md"
            py="sm"
            style={
              cssWidth !== undefined ? { width: cssWidth } : { minWidth: 160 }
            }
          >
            <CircleSpinner className="size-4 shrink-0" />
            <Text as="span" fontSize="sm">
              Uploading image…
            </Text>
          </Flex>
        ) : (
          <img
            alt=""
            className={cn(
              "block max-w-full rounded-sm cursor-default",
              selected && "ring-2 ring-blue-500 ring-offset-0",
            )}
            draggable={false}
            ref={imgRef}
            src={url}
            style={
              cssWidth !== undefined
                ? { width: cssWidth, maxHeight: 400 }
                : { width: "auto", maxHeight: 400 }
            }
          />
        )}
        {showHandles && !isUploading && (
          <>
            <ResizeHandle
              imgRef={imgRef}
              onWidthChange={handleWidthChange}
              onWidthCommit={handleWidthCommit}
              side="left"
            />
            <ResizeHandle
              imgRef={imgRef}
              onWidthChange={handleWidthChange}
              onWidthCommit={handleWidthCommit}
              side="right"
            />
          </>
        )}
      </div>
      {props.children}
    </PlateElement>
  );
});

const ResizeHandle = memo(function ResizeHandle({
  side,
  imgRef,
  onWidthChange,
  onWidthCommit,
}: {
  side: "left" | "right";
  imgRef: React.RefObject<HTMLImageElement | null>;
  onWidthChange: (width: number) => void;
  onWidthCommit: (width: number) => void;
}) {
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const computeWidth = useCallback(
    (clientX: number) => {
      const raw = clientX - startXRef.current;
      const delta = side === "right" ? raw : -raw;
      return Math.max(50, startWidthRef.current + delta);
    },
    [side],
  );

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      startXRef.current = e.clientX;
      startWidthRef.current = imgRef.current?.offsetWidth ?? 200;
    },
    [imgRef],
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) {
        return;
      }
      onWidthChange(computeWidth(e.clientX));
    },
    [computeWidth, onWidthChange],
  );

  const handlePointerUp = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) {
        return;
      }
      e.currentTarget.releasePointerCapture(e.pointerId);
      onWidthCommit(computeWidth(e.clientX));
    },
    [computeWidth, onWidthCommit],
  );

  return (
    <div
      className={cn(
        "absolute inset-y-0 flex w-4 cursor-ew-resize items-center justify-center",
        side === "left" ? "left-0 -translate-x-1/2" : "right-0 translate-x-1/2",
      )}
      contentEditable={false}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      <div className="flex h-8 w-2 flex-col items-center justify-center gap-0.5 rounded bg-white shadow-md border border-gray-300">
        <span className="h-1 w-1 rounded-full bg-gray-500" />
        <span className="h-1 w-1 rounded-full bg-gray-500" />
        <span className="h-1 w-1 rounded-full bg-gray-500" />
      </div>
    </div>
  );
});
